import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { useLocale } from "../context/LocaleContext";
import { ContentCard } from "../components/ContentCard";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { CalculatorsIndex } from "./calculators/Calculators";
import NotFound from "./NotFound";
import { LoadError } from "./DetailPage";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";

export default function SectionPage() {
  const { section } = useParams();
  const { locale, t, lp, resolveSection, setPageAlternates, config } = useLocale();
  const canonical = resolveSection(section);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1", 10);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!config || canonical === "calculators") return;
    setData(null); setNotFound(false); setError(false);
    api.get(`/api/public/sections/${section}?locale=${locale}&page=${page}`)
      .then(({ data }) => { setData(data); if (data.url_alternates) setPageAlternates(data.url_alternates); })
      .catch((err) => { if (err?.response?.status === 404) setNotFound(true); else setError(true); });
    track("page_view", { locale, path: `/${locale}/${section}` });
  }, [section, locale, page, canonical, config, setPageAlternates, reload]);

  if (canonical === "calculators") return <CalculatorsIndex />;

  if (notFound) return <NotFound />;
  if (error) return <LoadError onRetry={() => setReload((n) => n + 1)} t={t} />;

  const s = data?.section;

  return (
    <>
      {data && <Seo seo={data.seo} />}
      <section className="relative overflow-hidden border-b border-slate-200 bg-slate-50">
        {s?.image && (
          <div className="absolute inset-0">
            <img src={s.image} alt="" className="h-full w-full object-cover opacity-15" />
          </div>
        )}
        <div className="container-page relative py-14 lg:py-20">
          <Breadcrumbs items={[{ name: "LIVYVIO", to: lp("") }, { name: s?.title || section }]} />
          <h1 className="mt-4 max-w-3xl font-heading text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">
            {s?.title || section}
          </h1>
          {s?.description && <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">{s.description}</p>}
        </div>
      </section>

      <section className="container-page py-12">
        {!data ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => <div key={i} className="h-64 animate-pulse rounded-2xl bg-slate-100" />)}
          </div>
        ) : data.items.length === 0 ? (
          <p className="py-12 text-center text-slate-500">{t("comingSoon")}</p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {data.items.map((it, i) => <ContentCard key={it.id} item={it} index={i} />)}
            </div>
            {data.pages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-3">
                <Button variant="outline" className="rounded-full" disabled={page <= 1}
                  data-testid="pagination-prev"
                  onClick={() => setSearchParams({ page: String(page - 1) })}>‹</Button>
                <span className="text-sm text-slate-500">{page} / {data.pages}</span>
                <Button variant="outline" className="rounded-full" disabled={page >= data.pages}
                  data-testid="pagination-next"
                  onClick={() => setSearchParams({ page: String(page + 1) })}>›</Button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
